import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authContainer } from '../../di/container';
import type { AuthUser } from '../../domain/entities/auth.entity';
import { AuthContext, type AuthContextValue } from './auth-context';

/**
 * A sessão do app. Lê a sessão salva ao montar e expõe as operações; quem
 * consome é o `useAuth`.
 *
 * **Sair limpa o cache do TanStack Query**: ensaio, gabarito e ranking em
 * cache são da pessoa que saiu, e a próxima a entrar no mesmo navegador não
 * pode vê-los nem por um instante.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  useEffect(() => {
    let isMounted = true;
    void authContainer.repository
      .restoreSession()
      .catch(() => null)
      .then((restored) => {
        if (!isMounted) return;
        setUser(restored);
        setIsRestoring(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isRestoring,
      signIn: async (input) => {
        const signedIn = await authContainer.repository.signIn(input);
        setUser(signedIn);
        return signedIn;
      },
      signUp: async (input) => {
        const created = await authContainer.repository.signUp(input);
        setUser(created);
        return created;
      },
      completeRegistration: async (input) => {
        const completed =
          await authContainer.repository.completeRegistration(input);
        setUser(completed);
        // O que foi lido sem perfil (403) não vale mais.
        await queryClient.invalidateQueries();
        return completed;
      },
      markProfileMissing: () =>
        setUser((current) =>
          current && current.hasProfile
            ? { ...current, hasProfile: false }
            : current,
        ),
      forgotPassword: (email) => authContainer.repository.forgotPassword(email),
      signOut: async () => {
        await authContainer.repository.signOut();
        queryClient.clear();
        setUser(null);
      },
    }),
    [user, isRestoring, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
